import { useState, useMemo } from "react";

/**
 * usePagination — client-side pagination that preserves search / filter / sort on the caller.
 * Reusable across Mail Log, Audit Log, Pickup Entry, Inward Log, Outward Log, Parcels.
 * Returns:
 *   pageItems   — current page slice of `items`
 *   Pagination  — <Pagination /> JSX (drop in below the table)
 *   reset()     — resets to page 1 (call after search / filter change)
 */
export default function usePagination(items, { initialSize = 10, testid = "pagination" } = {}) {
  const [pageSize, setPageSize] = useState(initialSize);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => (items || []).slice((safePage - 1) * pageSize, safePage * pageSize), [items, safePage, pageSize]);

  const reset = () => setPage(1);

  const Pagination = () => (items?.length || 0) === 0 ? null : (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-2 text-xs" data-testid={testid}>
      <div className="text-muted-foreground">
        Showing <b className="text-foreground mono">{Math.min(items.length, (safePage-1)*pageSize + 1)}</b>–<b className="text-foreground mono">{Math.min(items.length, safePage*pageSize)}</b> of <b className="text-foreground mono">{items.length}</b>
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5">
          <span className="overline">Rows</span>
          <select data-testid={`${testid}-size`} value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs mono">
            {[10,20,30,40,50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="inline-flex border border-border rounded-md overflow-hidden">
          <button type="button" data-testid={`${testid}-prev`} disabled={safePage <= 1}
            onClick={() => setPage(p => Math.max(1, p-1))}
            className="px-3 py-1.5 text-xs disabled:opacity-40 hover:bg-primary hover:text-primary-foreground transition-colors">← Prev</button>
          <span className="px-3 py-1.5 text-xs mono border-l border-r border-border" data-testid={`${testid}-indicator`}>
            {safePage} / {totalPages}
          </span>
          <button type="button" data-testid={`${testid}-next`} disabled={safePage >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p+1))}
            className="px-3 py-1.5 text-xs disabled:opacity-40 hover:bg-primary hover:text-primary-foreground transition-colors">Next →</button>
        </div>
      </div>
    </div>
  );

  return { pageItems, Pagination, reset, page: safePage, pageSize };
}
